import { Injectable } from '@angular/core';
import {environment} from "../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {ParamService} from "./param.service";
import {IntegracionesService} from "./integraciones.service";
import {forkJoin} from "rxjs";

const apiUrl: string = environment.apiUrl;

export interface DomainInfo {
  valido: boolean;
  principal: boolean;
  marketplace: any;
  redireccionTiendaVirtual?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DomainService {
  private domainInfo: DomainInfo = {
    valido: true,
    principal: true,
    marketplace: null
  };
  private dominioCargado: boolean = false;
  private dominioCargadoSubject: Promise<void>;
  readonly rutasPublicas = [
    '/tienda',
    '/pages',
    '/error',
    '/s'
  ];

  constructor(
    private http: HttpClient,
    private paramService: ParamService,
    private integracionesService: IntegracionesService
  ) {
    this.dominioCargadoSubject = new Promise<void>((resolve) => {
      this.cargarDominioAsync(resolve);
    });
  }

  inicializarDominio(): Promise<void> {
    return this.dominioCargadoSubject;
  }

  private cargarDominioAsync(resolve: () => void) {
    const host = window.location.hostname;
    //console.log('HOST ANGULAR:', host);

    this.http.post<DomainInfo>(`${apiUrl}/adm/param/dominio/validar/`, {
      host: host
    }).subscribe({
      next: (response) => {
        //console.log('Dominio cargado:', response);
        this.domainInfo = response;

        // Validación adicional para marketplace con tienda virtual
        if (!response.principal && response.valido) {
          this.validarRedireccionTiendaVirtual(host, resolve);
        } else {
          this.dominioCargado = true;
          resolve();
        }
      },
      error: () => {
        console.error('Error validando dominio, usando valores por defecto');
        this.dominioCargado = true;
        resolve();
      }
    });
  }

  private extraerPathDeUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      // Incluir el hash si existe (para rutas Angular con #)
      return urlObj.pathname + urlObj.hash + urlObj.search;
    } catch (e) {
      // Si la URL no tiene protocolo, intentar extraer después del primer /
      const match = url.match(/^(https?:\/\/[^\/]+)?(\/.*)$/);
      return match ? match[2] : url;
    }
  }

  private validarRedireccionTiendaVirtual(host: string, resolve: () => void) {
    // Extraer dominio sin .com (manteniendo www. para primera búsqueda)
    const dominioSinExtension = host.replace('.com', '').replace('.co', '');

    // Verificar si la URL actual tiene solo el dominio (sin path/hash)
    const pathname = window.location.pathname;
    const hash = window.location.hash;
    // Considerar como solo dominio si está en /, /#/ o /#/error
    const esSoloDominio = pathname === '/' && (!hash || hash === '#' || hash === '#/' || hash === '#/error');
    // Consultar parámetros MARKETPLACE
    this.paramService.obtenerListaParametros(1 - 1, 1, 'MARKETPLACE', '').subscribe({
      next: (result) => {
        const marketplaces = result.data || [];
        // Primero buscar con formato exacto (con www. si aplica)
        let marketplaceEncontrado = marketplaces.find(
          (mp: any) => mp.valor === dominioSinExtension
        );
        if (marketplaceEncontrado) {
          // Validar tienda_padre primero (prioridad)
          if (marketplaceEncontrado.tienda_padre === 1 && esSoloDominio) {
            if (marketplaceEncontrado.enlace_tienda_padre) {
              this.domainInfo.redireccionTiendaVirtual = marketplaceEncontrado.enlace_tienda_padre;
              this.dominioCargado = true;
              window.location.replace(this.domainInfo.redireccionTiendaVirtual);
              resolve();
              return;
            }
          }

          // Si no es tienda_padre o la URL tiene path, validar marketplace_unico
          if (marketplaceEncontrado.marketplace_unico === 1) {
            // Consultar integraciones por dominio_marketplace
            const filtros = {
              page: 1,
              page_size: 1,
              marketplace: dominioSinExtension
            };
            this.integracionesService.obtenerListaIntegraciones(filtros).subscribe({
              next: (integracionesResult) => {
                const integraciones = integracionesResult.info || [];
                const canalConTienda = integraciones.find(
                  (int: any) => int.direccion_tienda_virtual
                );
                if (canalConTienda) {
                  // Extraer el path de la URL registrada y usar el dominio actual
                  const urlRegistrada = canalConTienda.direccion_tienda_virtual;
                  const pathExtraido = this.extraerPathDeUrl(urlRegistrada);
                  const dominioActual = window.location.hostname;
                  // Construir nueva URL con el dominio actual
                  const nuevaUrl = `https://${dominioActual}${pathExtraido}`;
                  // Guardar la URL de redirección en domainInfo
                  this.domainInfo.redireccionTiendaVirtual = nuevaUrl;
                  this.dominioCargado = true;
                  window.location.replace(this.domainInfo.redireccionTiendaVirtual);
                  resolve();
                } else {
                  console.log('No se encontró canal con dirección_tienda_virtual');
                  this.dominioCargado = true;
                  resolve();
                }
              },
              error: (err) => {
                console.error('Error consultando integraciones:', err);
                this.dominioCargado = true;
                resolve();
              }
            });
          } else {
            this.dominioCargado = true;
            resolve();
          }
        } else {
          console.log('No se encontró marketplace para el dominio');
          this.dominioCargado = true;
          resolve();
        }
      },
      error: (err) => {
        console.error('Error consultando parámetros MARKETPLACE:', err);
        this.dominioCargado = true;
        resolve();
      }
    });
  }

  cargarDominio() {
    const host = window.location.hostname;
    console.log('HOST ANGULAR:', host);


    return this.http.post<DomainInfo>(`${apiUrl}/adm/param/dominio/validar/`, {
      host: host
    });
  }

  setDomainInfo(info: DomainInfo) {
    this.domainInfo = info;
  }

  getDomainInfo(): DomainInfo {
    return this.domainInfo;
  }

  esPrincipal(): boolean {
    return this.domainInfo?.principal === true;
  }

  esDominioValido(): boolean {
    return this.domainInfo?.valido === true;
  }
}
